/**
 * supabaseStatementVersions.ts — CRUD-Schicht für statement_versions (C2).
 *
 * Tabelle ist append-only:
 *   - INSERT: erlaubt für Owner+Berater (RLS), generated_by muss = auth.uid().
 *   - SELECT: tenant-gefiltert (RLS).
 *   - UPDATE: niemals erlaubt (Trigger blockiert + keine Policy).
 *   - DELETE: nur Admin (Aufbewahrungs-Cleanup nach 10 Jahren).
 *
 * Kontext (siehe Migration 0013):
 *   Jede generierte Stellungnahme (DOCX oder PDF) wird mit Score-Snapshot,
 *   Platzhalter-JSON und Generator hier protokolliert. Das ist die formelle
 *   Beweiskette für B2G — welche Daten lagen welcher Stellungnahme zugrunde.
 *
 * Hinweis: Wir speichern keinen File-Blob (DSGVO-Datenminimierung — Blob
 * lebt im Endgerät). Wenn Re-Rendering nötig ist, baut der Aufrufer aus
 * den placeholders_json wieder die DOCX/HTML zusammen.
 */

import { getSupabaseClient } from './supabase';
import type {
  StatementTrafficLight,
  StatementVersionFormat,
  StatementVersionRow,
} from './supabaseTypes';

export interface CreateStatementVersionInput {
  readonly caseId: string;
  readonly format: StatementVersionFormat;
  readonly scoreValue: number;
  readonly scoreConfidence: number;
  readonly trafficLight: StatementTrafficLight;
  readonly verdict: string;
  readonly placeholders: Record<string, string>;
  readonly note?: string;
}

/**
 * Legt eine neue Statement-Version an. Generator = aktueller User
 * (durch Trigger erzwungen, hier nicht explizit gesetzt — Supabase
 * resolved auth.uid()).
 *
 * @throws Error wenn der User nicht eingeloggt ist oder RLS verbietet.
 */
export async function createStatementVersion(
  input: CreateStatementVersionInput,
): Promise<StatementVersionRow> {
  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`createStatementVersion: auth.getUser failed — ${userErr.message}`);
  }
  const userId = userData.user?.id;
  if (typeof userId !== 'string') {
    throw new Error('createStatementVersion: kein eingeloggter User');
  }

  // Cast `as never`: supabase-js v2 hat einen Type-Quirk, bei dem die
  // .insert()-Signatur in TS strict-Mode mit unserem handgepflegten
  // Database-Type kollidiert (siehe gleicher Workaround in
  // supabaseCaseReviews.ts). RLS+Trigger garantieren die Datenintegrität
  // zur Laufzeit — der Type-Bypass ist hier sicher.
  const insertPayload = {
    case_id: input.caseId,
    generated_by: userId,
    format: input.format,
    score_value: Math.round(input.scoreValue),
    score_confidence: Math.round(input.scoreConfidence),
    traffic_light: input.trafficLight,
    verdict: input.verdict,
    placeholders_json: input.placeholders,
    note: input.note ?? null,
  };
  const { data, error } = await sb
    .from('statement_versions')
    .insert(insertPayload as never)
    .select()
    .single();

  if (error) {
    throw new Error(`createStatementVersion [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Lädt alle Statement-Versionen eines Cases, neueste zuerst. RLS sorgt
 * dafür, dass nur Cases im eigenen Tenant sichtbar sind.
 */
export async function listStatementVersionsForCase(caseId: string): Promise<StatementVersionRow[]> {
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('statement_versions')
    .select('*')
    .eq('case_id', caseId)
    .order('generated_at', { ascending: false });

  if (error) {
    throw new Error(`listStatementVersionsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}
